import { App } from 'antd';
import { useMsal } from '@azure/msal-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  PreSalesCancellationReason,
  PreSalesHandoverType,
  PreSalesQuestion,
  PreSalesQuestionnaireContext,
} from '../../dto/model';
import { scopes } from '../../authConfig';
import { strings } from '../../lang';
import { heatBookingPageService } from '../../services/heat-booking-page-service';
import { GetAccessToken } from '../../utils/auth-utils';
import { PreSalesAnswers } from '../../utils/pre-sales-questionnaire-evaluator';
import { serializePreSalesAnswers } from './components/pre-sales-questionnaire-utils';
import { getPvStatus, isPvOnlyInterest, PvStatus } from './pre-sales-pv-handover';

type UsePreSalesPvHandoverParams = {
  handoverEnabled?: boolean;
  visibleQuestions: PreSalesQuestion[];
  answers: PreSalesAnswers;
  cancellationReasons: PreSalesCancellationReason[];
  hasCompletedCancellationEvaluation: boolean;
  isEvaluatingCancellation: boolean;
  cancellationEvaluationFailed: boolean;
  context?: PreSalesQuestionnaireContext;
};

type SuccessfulHandover = {
  signupId: string;
  handoverType: PreSalesHandoverType;
  customerKey: string;
};

export const usePreSalesPvHandover = ({
  handoverEnabled = true,
  visibleQuestions,
  answers,
  cancellationReasons,
  hasCompletedCancellationEvaluation,
  isEvaluatingCancellation,
  cancellationEvaluationFailed,
  context,
}: UsePreSalesPvHandoverParams) => {
  const { crmId, hpOrderNumber, signupId, gcid } = context ?? {};
  const { notification } = App.useApp();
  const { instance, inProgress } = useMsal();
  const customerKey = useMemo(
    () => JSON.stringify([
      crmId?.trim() ?? '',
      hpOrderNumber?.trim() ?? '',
      signupId?.trim() ?? '',
      gcid?.trim() ?? '',
    ]),
    [crmId, gcid, hpOrderNumber, signupId],
  );
  const [isQualifiedDismissed, setIsQualifiedDismissed] = useState(false);
  const [isDisqualifiedDismissed, setIsDisqualifiedDismissed] = useState(false);
  const [isForwarding, setIsForwarding] = useState(false);
  const [successfulHandover, setSuccessfulHandover] = useState<SuccessfulHandover | null>(null);
  const [isForwardingResultVisible, setIsForwardingResultVisible] = useState(false);
  const isMountedRef = useRef(false);
  const currentCustomerKeyRef = useRef(customerKey);
  currentCustomerKeyRef.current = customerKey;
  const successfulHandoverRef = useRef<SuccessfulHandover | null>(null);
  const forwardingSucceededRef = useRef(false);
  const forwardingPendingRef = useRef(false);
  const previousStatusRef = useRef<PvStatus>('NotInterested');
  const visibleQuestionKeys = useMemo(
    () => visibleQuestions.map((question) => question.questionKey),
    [visibleQuestions],
  );
  const isPvOnly = isPvOnlyInterest(answers.pv_interest);
  const hasHpCancellationReason = cancellationReasons.some(
    (reason) => reason.product === 'WP',
  );
  const handoverType: PreSalesHandoverType = hasHpCancellationReason
    ? 'hp_to_pv'
    : 'hp_to_heatx';

  const status = useMemo<PvStatus>(
    () => !hasCompletedCancellationEvaluation || isEvaluatingCancellation || cancellationEvaluationFailed
      ? 'NotInterested'
      : getPvStatus(answers, cancellationReasons, visibleQuestionKeys),
    [
      answers,
      cancellationEvaluationFailed,
      cancellationReasons,
      hasCompletedCancellationEvaluation,
      isEvaluatingCancellation,
      visibleQuestionKeys,
    ],
  );
  const isEligible = status === 'Eligible' || status === 'PvOnlyEligible';
  const isCancelled = status === 'Cancelled' || status === 'PvOnlyCancelled';

  const blockingError = !handoverEnabled
    ? undefined
    : !signupId?.trim()
      ? strings.preSalesQuestionnaire.pvMissingSignupId
      : !gcid?.trim()
        ? strings.preSalesQuestionnaire.pvMissingGcId
        : undefined;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setIsQualifiedDismissed(false);
    setIsDisqualifiedDismissed(false);
    setIsForwarding(false);
    setSuccessfulHandover(null);
    setIsForwardingResultVisible(false);
    successfulHandoverRef.current = null;
    forwardingSucceededRef.current = false;
    forwardingPendingRef.current = false;
    previousStatusRef.current = 'NotInterested';
  }, [customerKey]);

  useEffect(() => {
    if (
      !hasCompletedCancellationEvaluation
      || isEvaluatingCancellation
      || cancellationEvaluationFailed
    ) {
      return;
    }

    if (previousStatusRef.current !== status) {
      if (isEligible && !forwardingSucceededRef.current) {
        setIsQualifiedDismissed(false);
      }
      previousStatusRef.current = status;
    }
    if (!isCancelled) {
      setIsDisqualifiedDismissed(false);
    }
  }, [
    cancellationEvaluationFailed,
    hasCompletedCancellationEvaluation,
    isCancelled,
    isEligible,
    isEvaluatingCancellation,
    status,
  ]);

  const confirm = useCallback(async () => {
    const retainedHandover = successfulHandoverRef.current?.customerKey === customerKey
      ? successfulHandoverRef.current
      : null;
    if (
      !handoverEnabled
      || forwardingPendingRef.current
      || (forwardingSucceededRef.current && !retainedHandover)
      || !hasCompletedCancellationEvaluation
      || !isEligible
      || !crmId
      || !hpOrderNumber
      || !signupId?.trim()
      || !gcid?.trim()
    ) {
      return;
    }

    forwardingPendingRef.current = true;
    setIsForwarding(true);
    try {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      if (currentCustomerKeyRef.current !== customerKey) {
        return;
      }

      const effectiveHandoverType = retainedHandover?.handoverType ?? handoverType;
      const response = await heatBookingPageService.handoverPreSalesCustomer(
        {
          product: 'pv',
          handoverType: effectiveHandoverType,
          signupId,
          ...(retainedHandover && {
            existingHandoverSignupId: retainedHandover.signupId,
          }),
          gcId: gcid,
          identifier: { crmId, hpOrderNumber },
          formValues: serializePreSalesAnswers(answers),
          cancellation: {
            isFormCancelled: cancellationReasons.length > 0,
            cancellationReasons,
          },
        },
        token.accessToken,
      );
      if (currentCustomerKeyRef.current !== customerKey) {
        return;
      }

      if (!response.success) {
        const errorDetails = `${response.error ?? ''} ${response.message ?? ''}`;
        if (errorDetails.includes('LEAD_ALREADY_EXISTS')) {
          forwardingSucceededRef.current = true;
          setIsQualifiedDismissed(true);
          notification.success({
            message: strings.preSalesQuestionnaire.leadAlreadyHandedOver,
          });
          return;
        }

        notification.error({
          message: strings.preSalesQuestionnaire.pvForwardingError,
          description: response.error ?? response.message,
        });
        return;
      }

      const newSignUpId = response.data?.signupId?.trim();
      if (!newSignUpId) {
        notification.error({
          message: strings.preSalesQuestionnaire.pvForwardingError,
          description: strings.preSalesQuestionnaire.pvHandoverMissingResult,
        });
        return;
      }

      forwardingSucceededRef.current = true;
      const handoverResult = {
        signupId: newSignUpId,
        handoverType: effectiveHandoverType,
        customerKey,
      };
      successfulHandoverRef.current = handoverResult;
      setSuccessfulHandover(handoverResult);
      setIsForwardingResultVisible(true);
    } catch (error: any) {
      if (currentCustomerKeyRef.current === customerKey) {
        notification.error({
          message: strings.preSalesQuestionnaire.pvForwardingError,
          description: error?.message,
        });
      }
    } finally {
      if (currentCustomerKeyRef.current === customerKey) {
        if (isMountedRef.current) {
          setIsForwarding(false);
        }
        forwardingPendingRef.current = false;
      }
    }
  }, [
    answers,
    cancellationReasons,
    crmId,
    customerKey,
    gcid,
    handoverEnabled,
    hasCompletedCancellationEvaluation,
    handoverType,
    hpOrderNumber,
    inProgress,
    isEligible,
    instance,
    notification,
    signupId,
  ]);

  const submitOrForward = useCallback(async (submit: () => Promise<void>) => {
    if (handoverEnabled && isEligible) {
      await confirm();
      return;
    }

    await submit();
  }, [confirm, handoverEnabled, isEligible]);

  const resetForwardingResult = useCallback(() => {
    setIsForwarding(false);
    setIsForwardingResultVisible(false);
    forwardingPendingRef.current = false;
  }, []);

  const currentCustomerHandover = successfulHandover?.customerKey === customerKey
    ? successfulHandover
    : null;

  return {
    status,
    blockingError,
    isForwarding,
    forwardedSignUpId: currentCustomerHandover?.signupId ?? null,
    isForwardingResultVisible,
    handoverType: currentCustomerHandover?.handoverType ?? handoverType,
    isPvOnly,
    isQualifiedDialogOpen: handoverEnabled && isEligible && !isQualifiedDismissed,
    isDisqualifiedDialogOpen: isCancelled && !isDisqualifiedDismissed,
    confirm,
    submitOrForward,
    dismissQualified: () => setIsQualifiedDismissed(true),
    dismissDisqualified: () => setIsDisqualifiedDismissed(true),
    resetForwardingResult,
  };
};
